














































































































































































































import { Component, Vue, Prop, Watch } from "vue-property-decorator";
import { ScheduleStatus } from "@/api/models/ScheduleStatus";
import { CreateScheduleRequest } from "@/api/models/CreateScheduleRequest";
import { Department, Employee, Schedule, Shift } from "@/api";
import _ from "lodash";
import moment from "moment";
import { checkSelfRole } from "@/common/role_utils";

@Component
export default class ScheduleForm extends Vue {
  @Prop({ default: false }) value!: boolean;
  // eslint-disable-next-line
  @Prop({ default: () => null }) initialData!: any;

  valid = true;
  dateRangeMode = false;
  warningDialog = false;
  callOffDialog = false;
  callOffNote = "";
  weeklyHoursWarning = "";
  ScheduleStatus = ScheduleStatus;
  isOwner = checkSelfRole("owner");
  minDate = moment()
    .local()
    .format("YYYY-MM-DD");

  // eslint-disable-next-line
  formData: any = {
    startDate: "",
    endDate: "",
    startTime: undefined,
    endTime: undefined,
    status: ScheduleStatus.OPEN,
    departmentId: undefined,
    isUrgent: false,
    assignee: undefined,
    facilityId: undefined,
    shiftId: undefined,
  };

  statusOptions = Object.values(ScheduleStatus);
  required_select = (v: string) =>
    (v !== "" && v !== undefined && v != "0") || "Required";

  date = "";
  dateRange: string[] = [];

  get selectedDateLabel(): string {
    if (this.dateRange.length !== 0)
      return `Selected: ${moment(this.dateRange[0]).format(
        "MMM D, YYYY",
      )} - ${moment(this.dateRange[1]).format("MMM D, YYYY")}`;
    return `Selected: ${moment(this.date).format("MMM D, YYYY")}`;
  }

  async created() {
    const departments = this.$store.dispatch("company/loadDepartments");
    const locations = this.$store.dispatch("room/fetchCompanyRooms");
    const settings = this.$store.dispatch("schedule/fetchScheduleSettings");
    await Promise.all([departments, locations, settings]);
  }

  get dialog() {
    return this.value;
  }

  set dialog(val: boolean) {
    this.$emit("input", val);
  }

  clearCalendar() {
    const today = moment()
      .local()
      .format("YYYY-MM-DD");
    this.date = today;
    this.dateRange = [];
  }

  get scheduleSettings() {
    return this.$store.getters["schedule/scheduleSettings"];
  }

  get maxHourPerDay(): number | null {
    return this.scheduleSettings?.settings?.MaxHourPerDay || null;
  }

  get maxHourPerWeek(): number | null {
    return this.scheduleSettings?.settings?.MaxHourPerWeek || null;
  }

  private calcHours(s: { startTime?: string; endTime?: string }): number {
    if (!s.startTime || !s.endTime) return 8;
    const start = moment(s.startTime, "HH:mm:ss");
    const end = moment(s.endTime, "HH:mm:ss");
    if (end.isBefore(start)) end.add(1, "day");
    return parseFloat(end.diff(start, "hours", true).toFixed(1));
  }

  calculateUserWeeklyHours(): { totalHours: number; employeeName: string } {
    if (!this.formData.assignee || !this.formData.startDate) {
      return { totalHours: 0, employeeName: "" };
    }

    const employee = (this.$store.state.directory.employees || []).find(
      (e: Employee) => e.id === this.formData.assignee,
    );
    const employeeName = employee
      ? `${employee.firstName} ${employee.lastName}`
      : `Employee #${this.formData.assignee}`;

    const targetMoment = moment(this.formData.startDate);
    const startOfWeek = targetMoment.clone().startOf("week");
    const endOfWeek = targetMoment.clone().endOf("week");

    const allSchedules: Schedule[] = this.$store.state.schedule.schedules || [];

    const existingWeeklySchedules = allSchedules.filter((s: Schedule) => {
      if (s.assignee !== this.formData.assignee) return false;
      if (this.isEditing && s.id === this.initialData.id) return false;
      if (!s.date) return false;
      const d = moment(s.date);
      return (
        d.isSameOrAfter(startOfWeek, "day") &&
        d.isSameOrBefore(endOfWeek, "day")
      );
    });

    const existingHours = existingWeeklySchedules.reduce(
      (acc: number, s: Schedule) => acc + this.calcHours(s),
      0,
    );

    const daysInRange = this.formData.endDate
      ? moment(this.formData.endDate).diff(
          moment(this.formData.startDate),
          "days",
        ) + 1
      : 1;
    const newShiftHours =
      this.calcHours({
        startTime: this.formData.startTime,
        endTime: this.formData.endTime,
      }) * daysInRange;

    const totalHours = parseFloat((existingHours + newShiftHours).toFixed(1));
    return { totalHours, employeeName };
  }

  calculateUserDailyHours(): { shiftHours: number; employeeName: string } {
    if (!this.formData.assignee || !this.formData.startDate) {
      return { shiftHours: 0, employeeName: "" };
    }

    const employee = (this.$store.state.directory.employees || []).find(
      (e: Employee) => e.id === this.formData.assignee,
    );
    const employeeName = employee
      ? `${employee.firstName} ${employee.lastName}`
      : `Employee #${this.formData.assignee}`;

    const shiftHours = this.calcHours({
      startTime: this.formData.startTime,
      endTime: this.formData.endTime,
    });

    return { shiftHours, employeeName };
  }

  checkWeeklyHoursLimit(): boolean {
    if (!this.formData.assignee) return false;

    const warnings: string[] = [];

    // Daily check — only if a single date is scheduled and limit is configured and non-zero
    if (this.maxHourPerDay !== null && !this.dateRangeMode) {
      const { shiftHours, employeeName } = this.calculateUserDailyHours();
      if (shiftHours > this.maxHourPerDay) {
        warnings.push(
          `${employeeName} will work ${shiftHours}h on ${this.formData.startDate} (daily limit: ${this.maxHourPerDay}h).`,
        );
      }
    }

    // Weekly check — only if limit is configured and non-zero
    if (this.maxHourPerWeek !== null) {
      const { totalHours, employeeName } = this.calculateUserWeeklyHours();
      if (totalHours > this.maxHourPerWeek) {
        warnings.push(
          `${employeeName} will have ${totalHours}h total for the week (weekly limit: ${this.maxHourPerWeek}h).`,
        );
      }
    }

    if (warnings.length > 0) {
      this.weeklyHoursWarning = warnings.join(" ");
      return true;
    }
    this.weeklyHoursWarning = "";
    return false;
  }

  onSaveClick() {
    // eslint-disable-next-line
    if ((this.$refs.form as any).validate()) {
      if (!this.formData.startDate) {
        return;
      }
      if (this.checkWeeklyHoursLimit()) {
        this.warningDialog = true;
      } else {
        this.save();
      }
    }
  }

  confirmSave() {
    this.warningDialog = false;
    this.save();
  }

  onCallOff() {
    const schedule: Schedule = {
      ...this.initialData,
      departmentId: this.formData.departmentId,
      facilityId: this.formData.facilityId,
      shiftId: this.formData.shiftId,
      assignee: this.formData.assignee,
      status: this.formData.status,
      startTime: this.formData.startTime,
      endTime: this.formData.endTime,
      isUrgent: this.formData.isUrgent,
    };
    this.$store.dispatch("schedule/callOffSchedule", {
      schedule,
      note: this.callOffNote,
    });
    this.callOffDialog = false;
    this.callOffNote = "";
    this.dialog = false;
  }

  get departments(): Department[] {
    return this.$store.getters["company/departments"]?.map(
      (item: Department) => {
        return {
          ...item,
          header: undefined,
        };
      },
    );
  }

  get companyRooms() {
    // eslint-disable-next-line
    return this.$store.getters["room/companyRooms"]?.map((item: any) => {
      return {
        ...item,
        header: undefined,
      };
    });
  }

  get employees() {
    const employeesList = this.$store.state.directory.employees ?? [];
    const list = _.map(
      _.orderBy(employeesList, [
        employee => `${employee.firstName} ${employee.lastName}`.toLowerCase(),
      ]),
      employee => ({
        ...employee,
        text: `${employee.firstName} ${employee.lastName}`,
      }),
    );

    return list;
  }

  get isEditing() {
    return !!(this.initialData && this.initialData.id);
  }

  get isCalledOff(): boolean {
    const status = (this.initialData?.status || "").toUpperCase();
    return status === "CALLED_OFF" || status === "CANCELLED";
  }

  @Watch("dateRangeMode")
  onDateRangeModeChange(newVal: boolean) {
    if (newVal) {
      if (!this.dateRange[1]) {
        this.dateRange = [this.dateRange[0], this.dateRange[0]];
      }
    } else {
      this.dateRange = [];
    }
  }

  @Watch("date")
  onDateChange(newVal: string) {
    if (newVal) {
      this.formData.startDate = newVal;
      this.formData.endDate = newVal;
    }
  }

  @Watch("dateRange", { deep: true })
  onDateRangeChange(newVal: string[]) {
    if (newVal && newVal.length === 2) {
      this.formData.startDate = newVal[0];
      this.formData.endDate = newVal[1];
    }
  }

  @Watch("initialData", { deep: true })
  // eslint-disable-next-line
  onInitialDataChange(newVal: any) {
    if (newVal) {
      const startDate = newVal.date || newVal.startDate || "";
      const endDate = newVal.endDate || newVal.date || startDate;
      this.dateRangeMode = endDate !== startDate;
      this.date = startDate;
      this.dateRange = [startDate, endDate];
      this.formData = {
        ...newVal,
        startDate,
        endDate,
        startTime: newVal.startTime?.slice(0, 5),
        endTime: newVal.endTime?.slice(0, 5),
      };
    } else {
      this.resetForm();
    }
  }

  @Watch("value")
  onDialogChange(newVal: boolean) {
    if (newVal && !this.initialData) {
      this.resetForm();
    }
  }

  resetForm() {
    const today = new Date().toISOString().substr(0, 10);
    this.dateRangeMode = false;
    this.date = today;
    this.dateRange = [];
    this.weeklyHoursWarning = "";
    this.formData = {
      startDate: today,
      endDate: today,
      startTime: undefined,
      endTime: undefined,
      status: ScheduleStatus.OPEN,
      departmentId: undefined,
      isUrgent: false,
      assignee: undefined,
      facilityId: undefined,
    };
    if (this.$refs.form) {
      // eslint-disable-next-line
      (this.$refs.form as any).resetValidation();
    }
  }

  close() {
    if (!this.isEditing) {
      this.resetForm();
    } else {
      const startDate = this.initialData.date || this.initialData.startDate;
      const endDate =
        this.initialData.endDate || this.initialData.date || startDate;
      this.dateRangeMode = endDate !== startDate;
      this.date = startDate;
      this.dateRange = [startDate, endDate];
      this.formData = {
        ...this.initialData,
        startDate,
        endDate,
        startTime: this.initialData.startTime?.slice(0, 5),
        endTime: this.initialData.endTime?.slice(0, 5),
      };
    }
    this.dialog = false;
  }

  save() {
    this.dialog = false;
    const status = this.formData.assignee
      ? ScheduleStatus.ASSIGNED
      : ScheduleStatus.OPEN;

    if (this.isEditing) {
      const schedule: Schedule = {
        ...this.initialData,
        departmentId: this.formData.departmentId,
        facilityId: this.formData.facilityId,
        shiftId: this.formData.shiftId,
        assignee: this.formData.assignee,
        status,
        startTime: this.formData.startTime,
        endTime: this.formData.endTime,
        isUrgent: this.formData.isUrgent,
      };
      this.$store.dispatch("schedule/updateSchedule", {
        schedule,
        start: this.formData.startDate,
        end: this.formData.endDate,
      });
      return;
    }

    const schedule: CreateScheduleRequest = {
      companyId: 0,
      date: this.dateRangeMode ? "" : this.formData.startDate,
      startDate: this.formData.startDate,
      endDate: this.formData.endDate,
      startTime: this.formData.startTime,
      endTime: this.formData.endTime,
      status,
      departmentId: this.formData.departmentId,
      facilityId: this.formData.facilityId,
      shiftId: this.formData.shiftId,
      assignee: this.formData.assignee,
      isUrgent: this.formData.isUrgent,
    };
    this.$store.dispatch("schedule/addSchedule", {
      schedule,
      start: this.formData.startDate,
      end: this.formData.endDate,
    });
  }

  get shiftOptions(): Shift[] {
    return this.$store.getters["shift/shifts"] || [];
  }

  onAssigneeChange(assigneeId: number) {
    const employee = this.$store.state.directory.employees?.find(
      (employee: Employee) => employee.id === assigneeId,
    );
    if (employee) {
      const shift = this.shiftOptions.find(
        (shift: Shift) => shift.id === employee.shift,
      );

      this.formData.departmentId = employee.primaryDepartmentId;
      this.formData.shiftId = employee.shift;
      this.formData.startTime = shift?.startTime;
      this.formData.endTime = shift?.endTime;
    }
  }
}
